import {
  BrainCircuit,
  Bell,
  Boxes,
  Bug,
  Cloud,
  Coins,
  Code2,
  ClipboardCheck,
  Cpu,
  Database,
  FileText,
  FileCheck,
  Gauge,
  GitBranch,
  Globe,
  HardDrive,
  KeyRound,
  Layers,
  LifeBuoy,
  ListChecks,
  LockKeyhole,
  MonitorCog,
  Network,
  RadioTower,
  Repeat,
  Route,
  Rocket,
  Scale,
  ScanSearch,
  Server,
  ServerCog,
  Settings,
  ShieldAlert,
  ShieldCheck,
  TerminalSquare,
  Users,
  Waypoints,
  Wifi,
  Workflow,
  Wrench,
} from "lucide-react";

const containsAny = (value, words) => {
  const lower = String(value || "").toLowerCase();
  return words.some((word) => lower.includes(word));
};

export const getTaxonomyCategory = (name = "") => {
  if (containsAny(name, ["identity", "iam", "entra", "user", "group", "role", "rbac", "access", "auth"])) return "identity";
  if (containsAny(name, ["security", "compliance", "governance", "policy", "protect", "permission", "lock"])) return "security";
  if (containsAny(name, ["network", "vpc", "vnet", "dns", "route", "routing", "connectivity", "subnet", "vpn", "wireless"])) return "network";
  if (containsAny(name, ["storage", "s3", "blob", "disk", "file", "backup", "recovery"])) return "storage";
  if (containsAny(name, ["data", "database", "sql", "dynamodb", "rds", "redshift", "warehouse", "pipeline", "etl", "stream"])) return "data";
  if (containsAny(name, ["compute", "virtual machine", "vm", "ec2", "container", "kubernetes", "lambda", "serverless"])) return "compute";
  if (containsAny(name, ["monitor", "log", "observ", "operation", "alert", "incident", "cloudwatch", "maintain"])) return "operations";
  if (containsAny(name, ["cost", "billing", "budget", "pricing", "economics", "marketplace"])) return "cost";
  if (containsAny(name, ["deploy", "develop", "application", "api", "sdk", "code", "automation", "devops"])) return "development";
  if (containsAny(name, ["ai", "ml", "machine learning", "model", "generative"])) return "ai";
  if (containsAny(name, ["global", "region", "availability", "resilience", "migration"])) return "global";
  if (containsAny(name, ["cloud", "concept", "architecture", "solution"])) return "cloud";
  return "general";
};

const CATEGORY_ICONS = {
  identity: Users,
  security: ShieldCheck,
  network: Network,
  storage: HardDrive,
  data: Database,
  compute: Cpu,
  operations: MonitorCog,
  cost: Coins,
  development: TerminalSquare,
  ai: BrainCircuit,
  global: Globe,
  cloud: Cloud,
  general: GitBranch,
};

const DOMAIN_KEYWORD_ICONS = [
  { words: ["identity", "iam", "entra", "user", "group", "rbac"], icon: KeyRound },
  { words: ["governance", "management group", "subscription", "program management", "oversight"], icon: Scale },
  { words: ["threat", "vulnerab", "attack", "malicious", "mitigation"], icon: ShieldAlert },
  { words: ["architecture", "architect"], icon: Layers },
  { words: ["operation", "operate", "monitor", "observ", "incident", "alert", "maintain"], icon: Bell },
  { words: ["secure", "security", "protect", "compliance"], icon: ShieldCheck },
  { words: ["storage", "backup", "recovery", "s3", "blob", "disk"], icon: HardDrive },
  { words: ["compute", "virtual machine", "vm", "ec2", "server", "container"], icon: Server },
  { words: ["network access", "wireless", "wlan", "wifi"], icon: Wifi },
  { words: ["connectivity", "routing", "route", "ip connectivity"], icon: Route },
  { words: ["network", "vpc", "vnet", "subnet", "vpn"], icon: Network },
  { words: ["ip service", "dns", "dhcp", "nat"], icon: Waypoints },
  { words: ["data", "database", "warehouse", "pipeline", "analytics", "stream"], icon: Database },
  { words: ["develop", "application", "api", "sdk", "code"], icon: Code2 },
  { words: ["deploy", "release", "devops", "automation", "programmability"], icon: Workflow },
  { words: ["troubleshoot", "debug", "diagnostic"], icon: Bug },
  { words: ["performance", "high-performing", "optimiz", "scale", "capacity"], icon: Gauge },
  { words: ["cost", "billing", "pricing", "budget", "economics"], icon: Coins },
  { words: ["reliability", "resilien", "availability", "disaster"], icon: Repeat },
  { words: ["support", "service", "technology"], icon: LifeBuoy },
  { words: ["fundamental", "general", "concept"], icon: ClipboardCheck },
  { words: ["cloud"], icon: Cloud },
  { words: ["ai", "ml", "machine learning", "model", "generative"], icon: BrainCircuit },
];

const CATEGORY_ICON_POOLS = {
  identity: [KeyRound, Users, LockKeyhole, ClipboardCheck],
  security: [ShieldCheck, ShieldAlert, LockKeyhole, ScanSearch, FileCheck],
  network: [Network, Route, Wifi, RadioTower, Waypoints],
  storage: [HardDrive, Database, Boxes, Repeat],
  data: [Database, GitBranch, Workflow, HardDrive],
  compute: [Cpu, Server, ServerCog, Boxes],
  operations: [MonitorCog, Bell, Wrench, Gauge, ListChecks],
  cost: [Coins, Gauge, Scale, ClipboardCheck],
  development: [Code2, TerminalSquare, Workflow, Rocket],
  ai: [BrainCircuit, Cpu, ScanSearch],
  global: [Globe, RadioTower, Repeat],
  cloud: [Cloud, ServerCog, Layers],
  general: [GitBranch, ClipboardCheck, FileText, Layers],
};

const FALLBACK_DOMAIN_ICON_POOL = [
  ShieldCheck,
  Network,
  Database,
  Cpu,
  Cloud,
  Bell,
  Scale,
  Route,
  Workflow,
  Gauge,
  KeyRound,
  HardDrive,
  Layers,
  Code2,
  Repeat,
  Waypoints,
];

const SUBDOMAIN_CATEGORY_ICONS = {
  ...CATEGORY_ICONS,
  network: Route,
  operations: Settings,
  development: Workflow,
  global: RadioTower,
  general: FileText,
};

const SUBDOMAIN_KEYWORD_ICONS = [
  { words: ["vulnerab", "attack surface", "threat vector", "malicious"], icon: Bug },
  { words: ["incident response", "incident", "investigation", "forensic", "indicator"], icon: ScanSearch },
  { words: ["alert", "monitor", "logging", "observ", "analysis"], icon: Bell },
  { words: ["automation", "orchestration", "automate", "workflow"], icon: Workflow },
  { words: ["identity", "iam", "access management", "rbac", "user", "group", "role"], icon: KeyRound },
  { words: ["hardware", "software", "asset", "inventory"], icon: Boxes },
  { words: ["computing resources", "compute", "ec2", "virtual machine", "vm", "container", "server"], icon: ServerCog },
  { words: ["enterprise capabilities", "enhance security", "secure operations"], icon: Wrench },
  { words: ["governance", "policy", "program management", "oversight"], icon: Scale },
  { words: ["architecture", "architect", "design secure"], icon: Layers },
  { words: ["mitigation", "protect", "defense", "secure"], icon: ShieldAlert },
  { words: ["subnet", "routing", "route", "connectivity", "vpn"], icon: Route },
  { words: ["dns", "dhcp", "nat", "ip service"], icon: Waypoints },
  { words: ["wireless", "wifi", "wlan"], icon: Wifi },
  { words: ["storage", "backup", "recovery", "s3", "blob", "disk"], icon: HardDrive },
  { words: ["data source", "database", "sql", "analytics", "pipeline", "stream"], icon: Database },
  { words: ["performance", "optimiz", "scale", "capacity"], icon: Gauge },
  { words: ["cost", "billing", "budget", "pricing"], icon: Coins },
  { words: ["develop", "application", "api", "sdk", "code"], icon: Code2 },
  { words: ["deploy", "release", "devops"], icon: Rocket },
  { words: ["support", "service", "technology"], icon: LifeBuoy },
];

const FALLBACK_SUBDOMAIN_ICON_POOL = [
  FileText,
  ClipboardCheck,
  ShieldCheck,
  ShieldAlert,
  ScanSearch,
  KeyRound,
  LockKeyhole,
  Bell,
  Bug,
  Wrench,
  Workflow,
  Boxes,
  Database,
  ServerCog,
  Route,
  Waypoints,
  Gauge,
  ListChecks,
];

export const TAXONOMY_COLORS = {
  identity: "#60a5fa",
  security: "#a78bfa",
  network: "#22d3ee",
  storage: "#38bdf8",
  data: "#2dd4bf",
  compute: "#818cf8",
  operations: "#f59e0b",
  cost: "#fbbf24",
  development: "#34d399",
  ai: "#f472b6",
  global: "#06b6d4",
  cloud: "#7dd3fc",
  general: "#94a3b8",
};

const iconKey = (Icon) => Icon?.displayName || Icon?.name || String(Icon);

const keywordIconFor = (name = "") => {
  const lower = String(name || "").toLowerCase();
  const match = DOMAIN_KEYWORD_ICONS.find(({ words }) => words.some((word) => lower.includes(word)));
  return match?.icon || null;
};

const subdomainKeywordIconFor = (name = "") => {
  const lower = String(name || "").toLowerCase();
  const match = SUBDOMAIN_KEYWORD_ICONS.find(({ words }) => words.some((word) => lower.includes(word)));
  return match?.icon || null;
};

export const getDomainIconComponent = (name = "") =>
  keywordIconFor(name) || CATEGORY_ICONS[getTaxonomyCategory(name)] || ServerCog;

export const getUniqueDomainIconComponents = (domainNames = []) => {
  const used = new Set();
  return (domainNames || []).map((name, index) => {
    const category = getTaxonomyCategory(name);
    const candidates = [
      keywordIconFor(name),
      ...(CATEGORY_ICON_POOLS[category] || []),
      getDomainIconComponent(name),
      ...FALLBACK_DOMAIN_ICON_POOL,
    ].filter(Boolean);

    const selected = candidates.find((Icon) => !used.has(iconKey(Icon))) || candidates[index % candidates.length] || ServerCog;
    used.add(iconKey(selected));
    return selected;
  });
};

export const getSubdomainIconComponent = (name = "") =>
  subdomainKeywordIconFor(name) || SUBDOMAIN_CATEGORY_ICONS[getTaxonomyCategory(name)] || FileText;

export const getUniqueSubdomainIconComponents = (subdomainNames = []) => {
  const used = new Set();
  return (subdomainNames || []).map((name, index) => {
    const category = getTaxonomyCategory(name);
    const candidates = [
      subdomainKeywordIconFor(name),
      ...(CATEGORY_ICON_POOLS[category] || []),
      getSubdomainIconComponent(name),
      ...FALLBACK_SUBDOMAIN_ICON_POOL,
    ].filter(Boolean);

    const selected = candidates.find((Icon) => !used.has(iconKey(Icon)))
      || candidates[index % candidates.length]
      || FileText;
    used.add(iconKey(selected));
    return selected;
  });
};
export const getTaxonomyColor = (name = "") => TAXONOMY_COLORS[getTaxonomyCategory(name)] || TAXONOMY_COLORS.general;
